import { BASIC_COMPOSITION_TYPE } from "@constants";

/* ---------------------------------------------------------------------------
 * Kcal domain glossary — single source of truth for naming.
 *
 *   calorie  : raw data field coming from the back-office catalogue
 *              (e.g. product.calorie: { isVisible, content }). Always the
 *              shape, never a displayable value.
 *   kcal     : numeric value resolved from a `calorie` and ready to display
 *              (e.g. resolveKcal, getItemKcal, kcalText, KcalBadge).
 *   energy   : order-level aggregate with display metadata (totalKcal,
 *              hasVisibleKcal, isCalorieActive, ri%) — see useOrderEnergy
 *              and OrderEnergyBanner.
 *
 * When adding a new symbol, pick the term matching the abstraction level
 * above; do not introduce new terms (Calorie, EnergyValue, …) without
 * updating this glossary.
 * --------------------------------------------------------------------------*/

export type CalorieValue = {
    isVisible?: boolean;
    content?: number | string | null;
};

export type Composition = {
    quantity?: number;
    calorie?: CalorieValue;
    compositions?: Record<string, Composition>;
};

export type ShopCartStep = {
    compositions?: Record<string, Composition>;
};

export type CartItem = {
    quantity?: number;
    calorie?: CalorieValue;
    shopCart?: Record<string, ShopCartStep>;
    compositions?: Record<string, Composition>;
};

export function resolveKcal(
    node: CartItem | Composition | null | undefined
): number {
    if (!node || !node.calorie || node.calorie.isVisible !== true) return 0;
    const value = Number(node.calorie.content);
    return Number.isFinite(value) && value > 0 ? value : 0;
}

// Recursively sum kcal of an item AND its nested compositions, multiplied by
// each level's `quantity`. Basic-composition (ingredients in/out) is skipped:
// it's not a chosen step product, just a customization of an existing item.
// The top-level item's own quantity is NOT applied here (callers apply
// order-item quantity once at the top — see getOrderTotalKcal).
function sumCompositionsKcal(
    compositions: Record<string, Composition> | undefined
): number {
    if (!compositions) return 0;
    let total = 0;
    Object.keys(compositions).forEach((key) => {
        if (key === BASIC_COMPOSITION_TYPE) return;
        const node = compositions[key];
        if (!node) return;
        const qty = Number(node.quantity) || 1;
        const ownKcal = resolveKcal(node);
        const nestedKcal = sumCompositionsKcal(node.compositions);
        total += (ownKcal + nestedKcal) * qty;
    });
    return total;
}

export function getItemKcal(item: CartItem | null | undefined): number {
    if (!item) return 0;

    let total = resolveKcal(item);

    const shopCart = item.shopCart;
    if (shopCart && typeof shopCart === "object") {
        Object.keys(shopCart).forEach((stepKey) => {
            if (stepKey === BASIC_COMPOSITION_TYPE) return;
            total += sumCompositionsKcal(shopCart[stepKey]?.compositions);
        });
    }

    total += sumCompositionsKcal(item.compositions);

    return total;
}

export function getOrderTotalKcal(orderItems: CartItem[]): number {
    if (!Array.isArray(orderItems)) return 0;
    return orderItems.reduce((sum, item) => {
        const quantity = Number(item?.quantity) || 0;
        return sum + getItemKcal(item) * quantity;
    }, 0);
}

export function getRiPercent(totalKcal: number): number {
    if (!Number.isFinite(totalKcal) || totalKcal <= 0) return 0;
    return Math.round((totalKcal / 2000) * 100);
}

export function hasAnyVisibleKcal(orderItems: CartItem[]): boolean {
    if (!Array.isArray(orderItems)) return false;
    return orderItems.some((item) => getItemKcal(item) > 0);
}
